import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { CreditCard, Check, Star } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const packages = [
    {
      id: 'featured_listing',
      name: 'Featured Company Listing',
      price: 49,
      interval: 'month',
      description: 'Stand out in the Company Directory',
      features: [
        'Top placement in directory',
        'Featured badge',
        'Priority in search results',
        'Company logo highlighted',
        'Increased visibility'
      ],
      color: 'bg-blue-500',
      audience: 'companies'
    },
    {
      id: 'job_posting',
      name: 'Job Posting',
      price: 29,
      interval: 'one-time',
      description: 'Post a job opening',
      features: [
        'List one job opening',
        'Visible for 30 days',
        'Access to applicant pool',
        'Edit anytime',
        'Email notifications'
      ],
      color: 'bg-green-500',
      audience: 'employers'
    },
    {
      id: 'ad_bronze',
      name: 'Bronze Advertising',
      price: 99,
      interval: 'month',
      description: 'Get your brand noticed',
      features: [
        'Sidebar ad placement',
        '10,000 impressions/month',
        'Link to your website',
        'Performance analytics',
        'Cancel anytime'
      ],
      color: 'bg-amber-600',
      audience: 'advertisers'
    },
    {
      id: 'ad_silver',
      name: 'Silver Advertising',
      price: 199,
      interval: 'month',
      description: 'Premium visibility',
      features: [
        'Homepage banner ad',
        '25,000 impressions/month',
        'Priority placement',
        'Detailed analytics',
        'Social media mentions'
      ],
      color: 'bg-gray-400',
      audience: 'advertisers',
      popular: true
    },
    {
      id: 'ad_gold',
      name: 'Gold Advertising',
      price: 399,
      interval: 'month',
      description: 'Maximum exposure',
      features: [
        'All page banner rotation',
        '100,000+ impressions/month',
        'Featured in newsletter',
        'Sponsored content',
        'Dedicated account manager'
      ],
      color: 'bg-yellow-500',
      audience: 'advertisers'
    }
  ];

  const handleCheckout = async (packageId) => {
    if (!user) {
      toast.error('Please log in to continue');
      navigate('/login');
      return;
    }

    setLoading(packageId);

    try {
      const origin = window.location.origin;

      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: origin
        })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Payment initiation failed');
      }

      const data = await res.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-text_light py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <CreditCard size={56} weight="fill" className="text-accent mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            Pricing & Packages
          </h1>
          <p className="text-xl max-w-2xl mx-auto">
            Choose the perfect plan to grow your business in the pest control industry
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white rounded-lg shadow-lg border-2 ${
                pkg.popular ? 'border-primary' : 'border-borders'
              } p-8 flex flex-col relative`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    <Star size={16} weight="fill" /> POPULAR
                  </span>
                </div>
              )}

              <div className={`w-16 h-16 ${pkg.color} rounded-full flex items-center justify-center mb-4`}>
                <CreditCard size={32} weight="fill" className="text-white" />
              </div>

              <h3 className="text-2xl font-bold text-text_dark mb-2">{pkg.name}</h3>
              <p className="text-sm text-text_muted mb-4">{pkg.description}</p>

              <div className="mb-6">
                <span className="text-5xl font-bold text-primary">${pkg.price}</span>
                <span className="text-text_muted">
                  /{pkg.interval === 'month' ? 'mo' : 'post'}
                </span>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {pkg.features.map((feature, idx) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check size={20} weight="bold" className="text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-text_dark">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleCheckout(pkg.id)}
                disabled={loading === pkg.id}
                className={`w-full py-3 ${
                  pkg.popular ? 'bg-primary' : 'bg-gray-800'
                } text-white rounded-sm hover:opacity-90 transition-opacity font-bold disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading === pkg.id ? 'Processing...' : 'Get Started →'}
              </button>

              <p className="text-xs text-center text-text_muted mt-4">
                {pkg.interval === 'month' ? 'Cancel anytime' : 'One-time payment'}
              </p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-text_dark text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-borders">
              <h3 className="font-bold text-text_dark mb-2">What payment methods do you accept?</h3>
              <p className="text-sm text-text_muted">
                We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure Stripe payment processor.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-borders">
              <h3 className="font-bold text-text_dark mb-2">Can I cancel my subscription?</h3>
              <p className="text-sm text-text_muted">
                Yes! Monthly subscriptions can be canceled anytime. You&apos;ll retain access until the end of your billing period.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-borders">
              <h3 className="font-bold text-text_dark mb-2">How does the Featured Listing work?</h3>
              <p className="text-sm text-text_muted">
                Featured companies appear at the top of search results with a special badge and highlighted styling in the Company Directory.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-borders">
              <h3 className="font-bold text-text_dark mb-2">Do you offer refunds?</h3>
              <p className="text-sm text-text_muted">
                Monthly subscriptions are non-refundable, but you can cancel anytime. One-time job postings are refundable within 24 hours if unused.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-primary to-primary/80 text-white rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Questions about pricing?</h2>
          <p className="text-lg mb-6 opacity-90">
            Contact us for custom enterprise solutions or volume discounts
          </p>
          <a
            href="mailto:support@pestprohub.com"
            className="inline-block px-8 py-3 bg-white text-primary rounded-sm hover:bg-gray-100 transition-colors font-bold"
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
}
